
import { useState, useEffect, useMemo, useCallback, memo } from "react";
import { withRenderingDebug, useChildrenValidation } from "@/utils/errorDetector";
import { Category, Subcategory } from "@/types/category";
import { CategoryList } from "@/components/CategoryList";
import { ServiceList } from "@/components/ServiceList";
import { iconMapping } from "@/utils/iconMapping";
import { useCategoriesQuery } from "@/hooks/useCategoriesQuery";
import { useServicesQuery } from "@/hooks/useServicesQuery";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { PerformanceMonitor } from "@/components/PerformanceMonitor";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import { MobileErrorBoundary } from "@/components/ui/mobile-error-boundary";

interface HomeContentProps {
  searchQuery: string;
  selectedRegion: string;
}

const HomeContentComponent = memo(({ searchQuery, selectedRegion }: HomeContentProps) => {
  useChildrenValidation(null, 'HomeContent');
  
  // Reduce logging frequency to prevent excessive console output
  const logRender = useCallback(() => {
    if (Math.random() < 0.1) { // Only log 10% of renders
      console.log('[HomeContent Mobile Debug] Rendering with props:', {
        searchQuery,
        selectedRegion,
        isMobile: window.innerWidth < 768
      });
    }
  }, [searchQuery, selectedRegion]);
  
  logRender();
  const [activeCategory, setActiveCategory] = useState("All");
  const [activeSubcategoryId, setActiveSubcategoryId] = useState<number | null>(null);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [showAllInCategory, setShowAllInCategory] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 12;

  const networkStatus = useNetworkStatus();

  const { data: categoriesData, isLoading: categoriesLoading, isError: categoriesError } = useCategoriesQuery();
  const { 
    data: servicesData, 
    isLoading: servicesLoading, 
    isError: servicesError,
    refetch: refetchServices
  } = useServicesQuery(
    searchQuery, 
    activeSubcategoryId, 
    selectedRegion,
    page,
    pageSize
  );

  const services = servicesData?.services || [];
  const totalPages = servicesData?.totalPages || 1;
  const categories = categoriesData?.categories || [];
  const subcategories = categoriesData?.subcategories || [];

  const subcategoriesByParent = useMemo(() => {
    const result: { [key: number]: Subcategory[] } = {};
    subcategories.forEach((subcategory) => {
      if (!subcategory) return;
      
      const parentId = subcategory.parent_category;
      if (parentId === null || parentId === undefined) return;
  
      if (!result[parentId]) {
        result[parentId] = [];
      }
      result[parentId].push(subcategory);
    });
    return result;
  }, [subcategories]);

  // Get active subcategory label
  const activeSubcategoryLabel = useMemo(() => {
    if (!activeSubcategoryId) return null;
    const subcategory = subcategories.find(sub => sub.id === activeSubcategoryId);
    return subcategory?.label || null;
  }, [activeSubcategoryId, subcategories]);

  // Reset page when search params change
  useEffect(() => {
    setPage(1);
  }, [searchQuery, activeSubcategoryId, selectedRegion]);

  const handleCategoryClick = useCallback((category: string) => {
    if (expandedCategory === category) {
      setExpandedCategory(null);
    } else {
      setExpandedCategory(category);
      setActiveCategory(category);
      setShowAllInCategory(null);
    }
    setActiveSubcategoryId(null);
  }, [expandedCategory]);

  const handleSubcategoryClick = useCallback((subcategory: Subcategory) => {
    try {
      // Handle "All" category case
      if (subcategory.id === -1) {
        const categoryLabel = subcategory.label?.replace('All ', '') || '';
        setActiveCategory(categoryLabel);
        setActiveSubcategoryId(null);
        setShowAllInCategory(categoryLabel);
        return;
      }

      // Validate subcategory object
      if (!subcategory || typeof subcategory.id !== 'number') {
        console.error('Invalid subcategory object:', subcategory);
        return;
      }

      // Find the parent category for this subcategory
      const parentCategory = categories.find(cat => 
        subcategoriesByParent[cat.id]?.some(sub => sub.id === subcategory.id)
      );

      // Use React's built-in state batching
      setActiveCategory(parentCategory?.label || subcategory.label || '');
      setActiveSubcategoryId(subcategory.id);
      setShowAllInCategory(null);
    } catch (error) {
      console.error('Error in handleSubcategoryClick:', error);
      // Reset to safe state
      setActiveCategory("All");
      setActiveSubcategoryId(null);
      setShowAllInCategory(null);
    }
  }, [categories, subcategoriesByParent]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    window.scrollTo({
      top: document.getElementById('services-section')?.offsetTop - 120 || 0,
      behavior: 'smooth'
    });
  }, []);

  const hasErrors = categoriesError || servicesError;

  return (
    <MobileErrorBoundary componentName="HomeContent">
      <main className="container max-w-[1400px] pt-20 md:pt-40">
        <PerformanceMonitor />
        
        {hasErrors && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              There was a problem loading some content. Please refresh the page to try again.
            </AlertDescription>
          </Alert>
        )}
        
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4">Categories</h2>
          {categoriesError ? (
            <div className="p-4 bg-red-50 text-red-500 rounded-lg">
              Failed to load categories. Please refresh the page to try again.
            </div>
          ) : (
            <CategoryList
              loading={categoriesLoading}
              categories={categories}
              subcategories={subcategoriesByParent}
              activeCategory={activeCategory}
              expandedCategory={expandedCategory}
              activeSubcategoryId={activeSubcategoryId}
              iconMapping={iconMapping}
              onCategoryClick={handleCategoryClick}
              onSubcategoryClick={handleSubcategoryClick}
            />
          )}
        </div>

        <div id="services-section">
          <h2 className="text-lg font-semibold mb-4">
            {activeSubcategoryId 
              ? `Services in ${activeSubcategoryLabel || activeCategory}`
              : showAllInCategory
                ? `All ${showAllInCategory} Services`
                : searchQuery 
                  ? "Search Results" 
                  : "Featured Services"}
          </h2>
          {servicesError ? (
            <div className="p-4 bg-red-50 text-red-500 rounded-lg">
              Failed to load services. Please refresh the page to try again.
            </div>
          ) : (
            <ServiceList
              loading={servicesLoading}
              services={services}
              activeCategory={activeCategory}
              activeSubcategoryId={activeSubcategoryId}
              searchQuery={searchQuery}
              totalPages={totalPages}
              currentPage={page}
              onPageChange={handlePageChange}
              connectionStatus={networkStatus.type}
            />
          )}
        </div>
      </main>
    </MobileErrorBoundary>
  );
});

HomeContentComponent.displayName = "HomeContent";

export const HomeContent = HomeContentComponent;
